import type { EngineService } from './registry.js';

/**
 * The staging region Vinz reserves. It has two roles, so changing it changes both:
 * - it names the reserved region: in every message, in the deploy-job matcher, in discovery's
 *   workspace suffix and in the deploy plan's `reservedRegion`;
 * - it is the one deploy target of a Serverless/CDK service. Those deploy a region, not a terraform
 *   workspace, so the region itself stands in where an ECS service has its workspace names, and it
 *   keys their observation and `HIST#` rows (a change re-keys those rows, as it does the ECS ones).
 */
export const RESERVED_REGION = 'us-east-2';

/**
 * CI's staging region: it always runs `main`, and a hold never touches it. Only messages and comments name it;
 * the callers' deploys are what send `main` there - their own region expression, or the shared action's
 * `region_selection: computed` (#107).
 */
export const CI_REGION = 'us-west-2';

/**
 * The reserved region is everyone's default staging region, so a hold serves the holder's branch to everybody
 * on it, not just the holder. Said wherever a hold is taken or live - the grant reply, the Reserved post and
 * DM, the dashboard's held row - and never on a release or a refusal.
 */
export const RESERVED_IMPACT = `Everyone on ${RESERVED_REGION} staging now hits this branch.`;

/**
 * What a service's rows are keyed by: its ECS workspaces in the reserved region, or the region for a
 * Serverless/CDK service. Nothing for a service with no shared deploy workflow - there is no deploy to read.
 */
export function deployTargets(service: Pick<EngineService, 'path' | 'reservedWorkspaces'>): readonly string[] {
  if (service.reservedWorkspaces.length > 0) return service.reservedWorkspaces;
  return service.path === 'cdk' || service.path === 'serverless' ? [RESERVED_REGION] : [];
}

/**
 * The same service's targets in CI's region, read beside the reserved ones to catch a branch left there (vinz#114).
 * ECS uses the workspaces discovery found, never names derived from the reserved ones: a worker deployed to the
 * reserved region only has no twin, and a target that can never be found would keep every poll's walk open to its
 * limit. Before the first discovery that records them, an ECS service has none, so nothing is walked for it.
 */
export function ciTargets(service: Pick<EngineService, 'path' | 'reservedWorkspaces' | 'ciWorkspaces'>): readonly string[] {
  if (service.reservedWorkspaces.length > 0) return service.ciWorkspaces ?? [];
  return service.path === 'cdk' || service.path === 'serverless' ? [CI_REGION] : [];
}

/**
 * The refs CI itself deploys to its region. ECS callers also treat a `canary` push as a pipeline run, so canary
 * landing in CI's region is the design; anything else there is a finding. Serverless/CDK callers ship only `main`.
 */
export function ciPipelineRefs(service: Pick<EngineService, 'path'>): readonly string[] {
  return service.path === 'ecs' ? ['main', 'canary'] : ['main'];
}
